import type { SortTableId } from '@myrewards/contracts';
import { useCallback, useMemo, type RefObject } from 'react';
import type { FilterBarFilter, FilterBarProps, FilterOption } from './FilterBar';
import { sortLabel, sortRows, type SortColumn, type TableSort } from './tableSort';
import { useSavedSort } from './useSavedSort';
import { useTableSort, type TableFilter, type TableSortParams } from './useTableSort';

/** One filter on a work list (APP-149 D4): its choices, and which rows each lets through. */
export interface ListFilter<Row> {
  /** Its name on the button and the chip: "Claim". */
  name: string;
  /** Every choice it has, the one that shows everything first ("Anyone", "All stages"). */
  options: readonly Pick<FilterOption, 'value' | 'label'>[];
  /** Whether a row passes at a value; the first choice lets every row through. */
  passes: (row: Row, value: string) => boolean;
  /**
   * List every choice even when no row has it (a claim, a period). Otherwise the menu lists only
   * what the list holds, with the first choice and the one chosen even when nothing matches it.
   */
  listAll?: boolean;
  /** Its param in the address; the filter's id when left out. */
  param?: string;
}

type Values<F extends string> = Partial<Record<F, string>>;

const entries = <Row, F extends string>(
  filters: Readonly<Partial<Record<F, ListFilter<Row>>>>,
): [F, ListFilter<Row>][] =>
  Object.entries<ListFilter<Row> | undefined>(filters).filter(
    (e): e is [F, ListFilter<Row>] => e[1] !== undefined,
  );

const firstOf = <Row>(filter: ListFilter<Row>): string => filter.options[0]?.value ?? '';

/** The rows every set filter lets through; a filter at its first choice lets all through. */
export function filterRows<Row, F extends string>(
  rows: readonly Row[],
  filters: Readonly<Partial<Record<F, ListFilter<Row>>>>,
  values: Values<F>,
): Row[] {
  const set = entries(filters).flatMap(([id, filter]) => {
    const value = values[id];
    return value === undefined || value === firstOf(filter) ? [] : [{ filter, value }];
  });
  return rows.filter((row) => set.every(({ filter, value }) => filter.passes(row, value)));
}

/**
 * The bar's filters, with counts (D4). Each choice counts the rows it would show with the other
 * filters as they are, so a count is always what choosing it shows.
 */
export function filterBarFilters<Row, F extends string>(
  rows: readonly Row[],
  filters: Readonly<Partial<Record<F, ListFilter<Row>>>>,
  values: Values<F>,
): FilterBarFilter<F>[] {
  return entries(filters).map(([id, filter]) => {
    const others = Object.fromEntries(
      Object.entries(values).filter(([other]) => other !== id),
    ) as Values<F>;
    const base = filterRows(rows, filters, others);
    const first = firstOf(filter);
    const value = values[id] ?? first;
    const counted = filter.options.map((o) => ({
      ...o,
      count:
        o.value === first ? base.length : base.filter((row) => filter.passes(row, o.value)).length,
    }));
    return {
      id,
      name: filter.name,
      value,
      defaultValue: first,
      options:
        filter.listAll === true
          ? counted
          : counted.filter((o) => o.value === first || o.value === value || o.count > 0),
    };
  });
}

export interface ListTable<Row, F extends string> {
  /** The whole list, filtered then sorted: page it after this, never before (D3). */
  rows: Row[];
  sort: TableSort;
  setSort: (next: TableSort) => void;
  /** Each filter's value now. */
  values: Readonly<Record<F, string>>;
  /** Some filter is set. */
  filtered: boolean;
  clearFilters: () => void;
  /** "Clear filters" from an empty table: its button goes, so focus moves to the first filter. */
  clearFromEmpty: () => void;
  /** The sort and filters now, for keys that must change when the list does. */
  key: string;
  /** Everything the FilterBar needs but its label. */
  bar: Omit<FilterBarProps<F>, 'label'>;
}

/**
 * A work list's sort and filters (APP-149 D3, D4): kept in the address, opening at the person's
 * saved sort for `table` with Keep my sort (D7; `null` keeps none), applied to the whole list.
 * Ties go by `tieBreak` (the award number). The page pages `rows`, and spreads `bar` on a
 * FilterBar inside `barRef`.
 */
export function useListTable<Row, F extends string = never>({
  table,
  rows,
  columns,
  defaultSort,
  defaultWords,
  filters,
  tieBreak,
  order,
  params,
  barRef,
}: {
  table: SortTableId | null;
  rows: readonly Row[];
  /** The columns; those with `sort` are the ones that sort. */
  columns: readonly SortColumn<Row>[];
  defaultSort: TableSort;
  /** The default order in words, for "Back to oldest first". */
  defaultWords: string;
  filters?: Readonly<Partial<Record<F, ListFilter<Row>>>>;
  tieBreak?: (row: Row) => string;
  /**
   * The list's own ordering, for a list that isn't one row per line (Tax to enter's people,
   * grouped by PAA); left out, `sortRows` over `columns` with `tieBreak`.
   */
  order?: (rows: readonly Row[], sort: TableSort) => Row[];
  params?: TableSortParams;
  barRef: RefObject<HTMLElement | null>;
}): ListTable<Row, F> {
  const { saved, onSorted } = useSavedSort(table);
  const sortable = useMemo(
    () => columns.filter((column) => column.sort !== undefined).map((column) => column.id),
    [columns],
  );
  const defs = useMemo(
    () => filters ?? ({} as Readonly<Partial<Record<F, ListFilter<Row>>>>),
    [filters],
  );
  const strict = useMemo(
    () =>
      Object.fromEntries(
        entries(defs).map(([id, filter]) => [
          id,
          {
            values: filter.options.map((o) => o.value),
            default: firstOf(filter),
            ...(filter.param !== undefined && { param: filter.param }),
          },
        ]),
      ) as unknown as Record<F, TableFilter>,
    [defs],
  );
  const state = useTableSort<F>({
    defaultSort,
    sortable,
    filters: strict,
    saved,
    onSorted,
    ...(params !== undefined && { params }),
  });
  const shown = useMemo(() => {
    const kept = filterRows(rows, defs, state.filters);
    return order === undefined
      ? sortRows(kept, columns, state.sort, tieBreak)
      : order(kept, state.sort);
  }, [rows, defs, state.filters, columns, state.sort, tieBreak, order]);
  const { clearFilters } = state;
  const clearFromEmpty = useCallback(() => {
    barRef.current?.querySelector<HTMLElement>('button')?.focus();
    clearFilters();
  }, [barRef, clearFilters]);
  const filtered = entries(defs).some(([id, filter]) => state.filters[id] !== firstOf(filter));
  return {
    rows: shown,
    sort: state.sort,
    setSort: state.setSort,
    values: state.filters,
    filtered,
    clearFilters,
    clearFromEmpty,
    key: JSON.stringify([state.sort, state.filters]),
    bar: {
      filters: filterBarFilters(rows, defs, state.filters),
      onChange: state.setFilter,
      onClearFilters: clearFilters,
      shown: shown.length,
      total: rows.length,
      // In words over the whole list, never one page (sortLabel's own rule).
      sortWords: sortLabel(columns, state.sort, rows),
      sortIsDefault: state.isDefaultSort,
      defaultSortLabel: defaultWords,
      onResetSort: state.resetSort,
    },
  };
}
